import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { DemoTranslations } from './demoTranslations';

interface Transaction {
  id: string;
  type: 'income' | 'expense';
  description: string;
  category: string;
  categoryColor: string;
  amount: number;
  date: string;
  isNew?: boolean;
}

interface DemoDashboardProps {
  newTransaction?: {
    description: string;
    amount: number;
    category: string;
  };
  onAnimationComplete?: () => void;
  translations: DemoTranslations;
}

const CATEGORY_COLORS: Record<string, string> = {
  food: '#EF4444',
  transport: '#F97316',
  housing: '#3B82F6',
  salary: '#22C55E',
  freelance: '#3B82F6',
  education: '#14B8A6',
  health: '#22C55E',
  entertainment: '#84CC16',
};

export const DemoDashboard: React.FC<DemoDashboardProps> = ({
  newTransaction,
  onAnimationComplete,
  translations: t,
}) => {
  // Initial summary from translations (market-specific values)
  const INITIAL_SUMMARY = useMemo(() => ({
    balance: t.dashboardValues.balance,
    income: t.dashboardValues.income,
    expense: t.dashboardValues.expense,
    count: t.dashboardValues.count,
  }), [t]);

  // Demo transactions - generated based on translations
  const DEMO_TRANSACTIONS: Transaction[] = useMemo(() =>
    t.dashboardValues.transactions.map((tx, index) => ({
      id: String(index + 1),
      type: tx.type,
      description: tx.description,
      category: getCategoryLabelFromKey(tx.category),
      categoryColor: CATEGORY_COLORS[tx.category] || '#8B5CF6',
      amount: tx.amount,
      date: tx.date,
    }))
  , [t]);

  // Category labels lookup using translations
  function getCategoryLabelFromKey(category: string): string {
    const categoryMap: Record<string, string> = {
      food: t.categories.food,
      transport: t.categories.transport,
      housing: t.categories.housing,
      salary: t.categories.salary,
      freelance: t.categories.freelance,
      education: t.categories.education,
      health: t.categories.health,
      entertainment: t.categories.entertainment,
    };
    return categoryMap[category] || category;
  }

  const [transactions, setTransactions] = useState<Transaction[]>([...DEMO_TRANSACTIONS]);
  const [summary, setSummary] = useState({ ...INITIAL_SUMMARY });
  const [showNewTransaction, setShowNewTransaction] = useState(false);
  const [animateCards, setAnimateCards] = useState(false);
  const [hasAddedTransaction, setHasAddedTransaction] = useState(false);

  // Reset state when component mounts (triggered by key change)
  useEffect(() => {
    setTransactions([...DEMO_TRANSACTIONS]);
    setSummary({ ...INITIAL_SUMMARY });
    setShowNewTransaction(false);
    setHasAddedTransaction(false);
    setAnimateCards(false);
    // Animate cards on mount
    setTimeout(() => setAnimateCards(true), 300);
  }, [DEMO_TRANSACTIONS, INITIAL_SUMMARY]);

  useEffect(() => {
    if (newTransaction && !hasAddedTransaction) {
      // Add new transaction with animation (only once)
      const newTx: Transaction = {
        id: 'new',
        type: 'expense',
        description: newTransaction.description,
        category: getCategoryLabelFromKey(newTransaction.category),
        categoryColor: CATEGORY_COLORS[newTransaction.category] || '#8B5CF6',
        amount: newTransaction.amount,
        date: t.dashboard.now,
        isNew: true,
      };

      setHasAddedTransaction(true);

      setTimeout(() => {
        setShowNewTransaction(true);
        setTransactions([newTx, ...DEMO_TRANSACTIONS.slice(0, 3)]);
        setSummary({
          balance: INITIAL_SUMMARY.balance - newTransaction.amount,
          income: INITIAL_SUMMARY.income,
          expense: INITIAL_SUMMARY.expense + newTransaction.amount,
          count: INITIAL_SUMMARY.count + 1,
        });
      }, 500);

      setTimeout(() => {
        if (onAnimationComplete) {
          onAnimationComplete();
        }
      }, 3000);
    }
  }, [newTransaction, hasAddedTransaction, DEMO_TRANSACTIONS, INITIAL_SUMMARY, t]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(t.locale, {
      style: 'currency',
      currency: t.currency,
    }).format(value);
  };

  // Pie chart data
  const pieData = useMemo(() => [
    { category: t.categories.travel, percentage: 45, color: '#6366F1' },
    { category: t.categories.housing, percentage: 27, color: '#3B82F6' },
    { category: t.categories.bills, percentage: 10, color: '#06B6D4' },
    { category: t.categories.education, percentage: 6, color: '#14B8A6' },
    { category: t.categories.others, percentage: 12, color: '#8B5CF6' },
  ], [t]);

  return (
    <div className="w-full max-w-[375px] h-[812px] bg-gray-900 rounded-[40px] overflow-hidden shadow-2xl border-[8px] border-gray-800 relative">
      {/* iPhone notch */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[150px] h-[30px] bg-gray-800 rounded-b-[20px] z-20" />

      {/* Status bar */}
      <div className="pt-10 px-6 pb-4 bg-gray-900">
        <h1 className="text-white text-xl font-bold">{t.dashboard.title}</h1>
        <p className="text-gray-400 text-sm">{t.dashboard.subtitle}</p>
      </div>

      {/* Content */}
      <div className="px-4 pb-4 overflow-y-auto h-[calc(100%-120px)] space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          {/* Balance */}
          <div
            className={`bg-gray-800 rounded-xl p-4 transform transition-all duration-500 ${
              animateCards ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-xs">{t.dashboard.balance}</span>
              <DollarSign className="w-4 h-4 text-purple-400" />
            </div>
            <p className={`text-white font-bold text-lg transition-all duration-300 ${
              showNewTransaction ? 'text-yellow-400' : ''
            }`}>
              {formatCurrency(summary.balance)}
            </p>
          </div>

          {/* Income */}
          <div
            className={`bg-gray-800 rounded-xl p-4 transform transition-all duration-500 delay-100 ${
              animateCards ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-xs">{t.dashboard.income}</span>
              <TrendingUp className="w-4 h-4 text-green-400" />
            </div>
            <p className="text-green-400 font-bold text-lg">
              {formatCurrency(summary.income)}
            </p>
          </div>

          {/* Expenses */}
          <div
            className={`bg-gray-800 rounded-xl p-4 transform transition-all duration-500 delay-200 ${
              animateCards ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-xs">{t.dashboard.expenses}</span>
              <TrendingDown className="w-4 h-4 text-red-400" />
            </div>
            <p className={`text-red-400 font-bold text-lg transition-all duration-300 ${
              showNewTransaction ? 'scale-110' : ''
            }`}>
              {formatCurrency(summary.expense)}
            </p>
          </div>

          {/* Count */}
          <div
            className={`bg-gray-800 rounded-xl p-4 transform transition-all duration-500 delay-300 ${
              animateCards ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-xs">{t.dashboard.transactions}</span>
              <Receipt className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-white font-bold text-lg">{summary.count}</p>
          </div>
        </div>

        {/* Pie Chart */}
        <div
          className={`bg-gray-800 rounded-xl p-4 transform transition-all duration-500 delay-400 ${
            animateCards ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
        >
          <h3 className="text-white font-medium mb-4">{t.dashboard.byCategory}</h3>
          <div className="flex items-center gap-4">
            {/* Simple pie visualization */}
            <div className="relative w-24 h-24">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                {pieData.reduce(
                  (acc, item, i) => {
                    const startAngle = acc.offset;
                    const endAngle = startAngle + (item.percentage / 100) * 360;
                    const largeArc = item.percentage > 50 ? 1 : 0;
                    const startX = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                    const startY = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                    const endX = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                    const endY = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);

                    acc.paths.push(
                      <path
                        key={i}
                        d={`M 50 50 L ${startX} ${startY} A 40 40 0 ${largeArc} 1 ${endX} ${endY} Z`}
                        fill={item.color}
                        className="transition-all duration-500"
                        style={{
                          opacity: animateCards ? 1 : 0,
                          transitionDelay: `${500 + i * 100}ms`,
                        }}
                      />
                    );
                    acc.offset = endAngle;
                    return acc;
                  },
                  { paths: [] as React.ReactNode[], offset: 0 }
                ).paths}
                <circle cx="50" cy="50" r="25" fill="#1F2937" />
              </svg>
            </div>

            {/* Legend */}
            <div className="flex-1 space-y-1">
              {pieData.slice(0, 4).map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-gray-400 text-xs">{item.category}</span>
                  </div>
                  <span className="text-gray-300 text-xs">{item.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div
          className={`bg-gray-800 rounded-xl p-4 transform transition-all duration-500 delay-500 ${
            animateCards ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
        >
          <h3 className="text-white font-medium mb-3">{t.dashboard.recentTransactions}</h3>
          <div className="space-y-3">
            {transactions.map((tx, i) => (
              <div
                key={tx.id}
                className={`flex items-center justify-between p-2 rounded-lg transition-all duration-500 ${
                  tx.isNew
                    ? 'bg-purple-500/20 border border-purple-500/50 animate-pulse'
                    : 'bg-gray-700/50'
                }`}
                style={{
                  animationDelay: `${i * 100}ms`,
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${tx.categoryColor}20` }}
                  >
                    {tx.type === 'income' ? (
                      <ArrowUpRight className="w-4 h-4" style={{ color: tx.categoryColor }} />
                    ) : (
                      <ArrowDownRight className="w-4 h-4" style={{ color: tx.categoryColor }} />
                    )}
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{tx.description}</p>
                    <p className="text-gray-400 text-xs">
                      {tx.category} - {tx.date}
                    </p>
                  </div>
                </div>
                <p
                  className={`font-medium text-sm ${
                    tx.type === 'income' ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {tx.type === 'income' ? '+' : '-'}
                  {formatCurrency(tx.amount)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom nav mock */}
      <div className="absolute bottom-0 left-0 right-0 bg-gray-800 px-6 py-4 pb-8 flex justify-around">
        <div className="flex flex-col items-center">
          <div className="w-6 h-6 bg-purple-500 rounded-lg" />
          <span className="text-purple-400 text-xs mt-1">{t.dashboard.nav.home}</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-6 h-6 bg-gray-600 rounded-lg" />
          <span className="text-gray-400 text-xs mt-1">{t.dashboard.nav.transactions}</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-6 h-6 bg-gray-600 rounded-lg" />
          <span className="text-gray-400 text-xs mt-1">{t.dashboard.nav.pots}</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-6 h-6 bg-gray-600 rounded-lg" />
          <span className="text-gray-400 text-xs mt-1">{t.dashboard.nav.profile}</span>
        </div>
      </div>
    </div>
  );
};

export default DemoDashboard;
